import React from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { useAuthState, useAuthActions } from '@/utils/auth';
import {
  useRejectInvitationMutation,
  useAcceptInvitationMutation,
  MyUserDataFragment,
  Maybe,
} from '@/generated/codegen';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';

type RolesNotificationsTableProps = {
  singleCol?: boolean;
};

type Definitely<R> =
  R extends Maybe<infer S> ? (Maybe<S> extends R ? never : S) : never;

export const RolesNotificationsTable: React.FC<
  RolesNotificationsTableProps
> = ({ singleCol = false }) => {
  const [acceptInvitation] = useAcceptInvitationMutation();
  const [rejectInvitation] = useRejectInvitationMutation();
  const { user } = useAuthState();
  const { refreshUser } = useAuthActions();
  const [loading, setLoading] = React.useState(false);

  const onAcceptInvitation = (
    invitation: Definitely<MyUserDataFragment['invitations'][number]>
  ) => {
    setLoading(true);
    acceptInvitation({
      variables: { id: invitation.id },
    })
      .then(() => refreshUser())
      .finally(() => setLoading(false));
  };

  const onRejectInvitation = (
    invitation: Definitely<MyUserDataFragment['invitations'][number]>
  ) => {
    setLoading(true);
    rejectInvitation({
      variables: { id: invitation.id },
    })
      .then(() => refreshUser())
      .finally(() => setLoading(false));
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {singleCol ? (
            <>
              <TableHead>Invitacion</TableHead>
              <TableHead>Aceptar</TableHead>
            </>
          ) : (
            <>
              <TableHead>Compañia</TableHead>
              <TableHead>Alcance</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Aceptar</TableHead>
            </>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {user?.invitations?.map(
          (invitation, index) =>
            invitation && (
              <TableRow key={index}>
                {singleCol ? (
                  <TableCell>
                    {invitation.label} en{' '}
                    {invitation.project
                      ? `${invitation.project.name} (${invitation.company.name})`
                      : invitation.business_unit
                        ? `${invitation.business_unit.name} (${invitation.company.name})`
                        : invitation.company.name}
                  </TableCell>
                ) : (
                  <>
                    <TableCell>{invitation.company.name}</TableCell>
                    <TableCell>
                      {invitation.project?.name ??
                        invitation.business_unit?.name ??
                        '-'}
                    </TableCell>
                    <TableCell>{invitation.label}</TableCell>
                  </>
                )}
                <TableCell>
                  <div
                    style={{
                      display: 'inline-flex',
                    }}
                  >
                    <Button
                      variant="success-outline"
                      onClick={() => onAcceptInvitation(invitation)}
                      disabled={loading}
                      size="sm"
                      role="aceptar"
                    >
                      {loading ? <LoadingSpinner /> : <FaCheck />}
                    </Button>
                    <div style={{ width: '4px', height: '4px' }} />
                    <Button
                      variant="destructive-outline"
                      onClick={() => onRejectInvitation(invitation)}
                      disabled={loading}
                      size="sm"
                      role="rechazar"
                    >
                      {loading ? <LoadingSpinner /> : <FaTimes />}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )
        )}
      </TableBody>
    </Table>
  );
};
